import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import { CATEGORY_VALUE_TO_LABEL } from '../../constants/product-categories'

interface ProductDetail {
  id: string
  name: string
  slug: string
  category: string
  description: string
  price: number | string
  compareAtPrice?: number | string
  quantity: number
  inStock: boolean
  weight: number
  width?: number | null
  height?: number | null
  length?: number | null
  parcelValue?: number | null
  sku?: string
  barcode?: string
  images: string[]
  attributes?: {
    material?: string
    color?: string
    size?: string
  }
  isActive: boolean
  isFeatured: boolean
  views: number
  totalSales: number
  vendorId: string
  createdAt: string
  updatedAt: string
}

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [product, setProduct] = useState<ProductDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchProduct(id)
    }
  }, [id])

  const fetchProduct = async (productId: string) => {
    try {
      setLoading(true)
      const response = await api.get(`/souvenir-products/${productId}`)
      setProduct(response.data)
    } catch (err: any) {
      console.error('Error fetching product:', err)
      setError(err.response?.data?.message || 'Failed to fetch product')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!product || !confirm('Are you sure you want to delete this product?')) return

    try {
      await api.delete(`/souvenir-products/${product.id}`)
      navigate('/vendor-dashboard/products')
    } catch (err: any) {
      console.error('Error deleting product:', err)
      alert('Failed to delete product')
    }
  }

  const handleToggleActive = async () => {
    if (!product) return

    try {
      const response = await api.put(`/souvenir-products/${product.id}`, {
        isActive: !product.isActive,
      })
      setProduct(response.data)
    } catch (err: any) {
      console.error('Error updating product:', err)
      alert('Failed to update product')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Link
            to="/vendor-dashboard/products"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Products
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          Product not found
        </div>
      </div>
    )
  }

  const price = typeof product.price === 'number' ? product.price : parseFloat(product.price)
  const compareAtPrice = product.compareAtPrice != null ? Number(product.compareAtPrice) : null

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/vendor-dashboard/products"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Products
          </Link>
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              product.isActive
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {product.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <p className="text-gray-600 mt-1">SKU: {product.sku || 'N/A'}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/vendor/products/${product.id}/edit`}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <PencilIcon className="h-4 w-4" />
            Edit
          </Link>
          <button
            onClick={handleToggleActive}
            className={`px-4 py-2 rounded-lg transition ${
              product.isActive
                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {product.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
          >
            <TrashIcon className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Product Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Images */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Images</h3>
            {product.images && product.images.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {product.images.map((url, index) => (
                  <img
                    key={index}
                    src={url}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                ))}
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">No images</p>
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="mt-1">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {CATEGORY_VALUE_TO_LABEL[product.category] || product.category}
                    </span>
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.description}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">SKU</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.sku || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Barcode</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.barcode || 'N/A'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Pricing & Inventory */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Pricing & Inventory</h3>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Price</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    RM{price.toFixed(2)}
                  </dd>
                </div>
                {compareAtPrice && compareAtPrice > price && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Compare At Price</dt>
                    <dd className="mt-1 text-lg text-gray-500 line-through">
                      RM{compareAtPrice.toFixed(2)}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Stock</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {product.quantity} {product.inStock ? '(In Stock)' : '(Out of Stock)'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Attributes */}
          {product.attributes && (product.attributes.material || product.attributes.color || product.attributes.size) && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Attributes</h3>
              </div>
              <div className="p-6">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3">
                  {product.attributes.material && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Material</dt>
                      <dd className="mt-1 text-sm text-gray-900">{product.attributes.material}</dd>
                    </div>
                  )}
                  {product.attributes.color && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Color</dt>
                      <dd className="mt-1 text-sm text-gray-900">{product.attributes.color}</dd>
                    </div>
                  )}
                  {product.attributes.size && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Size</dt>
                      <dd className="mt-1 text-sm text-gray-900">{product.attributes.size}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          )}

          {/* Shipping & Dimensions */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Shipping &amp; Dimensions</h3>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Weight (KG)</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.weight != null ? Number(product.weight).toFixed(2) : 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Width (CM)</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.width != null ? Number(product.width).toFixed(2) : 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Height (CM)</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.height != null ? Number(product.height).toFixed(2) : 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Length (CM)</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.length != null ? Number(product.length).toFixed(2) : 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Parcel Value (RM)</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.parcelValue != null ? Number(product.parcelValue).toFixed(2) : 'N/A'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Performance</h3>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-3">
                <div className="bg-blue-50 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
                  <dt className="text-sm font-medium text-blue-500 truncate">Total Views</dt>
                  <dd className="mt-1 text-3xl font-semibold text-blue-900">{product.views}</dd>
                </div>
                <div className="bg-green-50 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
                  <dt className="text-sm font-medium text-green-500 truncate">Total Sales</dt>
                  <dd className="mt-1 text-3xl font-semibold text-green-900">{product.totalSales}</dd>
                </div>
                <div className="bg-purple-50 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
                  <dt className="text-sm font-medium text-purple-500 truncate">Revenue</dt>
                  <dd className="mt-1 text-3xl font-semibold text-purple-900">
                    RM{(price * product.totalSales).toFixed(2)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Metadata</h3>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Product ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">{product.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Slug</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">{product.slug}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(product.createdAt).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(product.updatedAt).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Featured</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {product.isFeatured ? 'Yes' : 'No'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetailPage
